import { SummaryData } from "./DrillingSummary"
import { defaultHoleDescription } from "./MineDrilling"
import type { HoleDescription } from "./HoleDescriptionForm"
import type { MatrixData } from "./Matrix"
import { GeneralInformation, DrillEquipment } from "./types"
import { DateTime } from "luxon"
import { Api } from "@/types/api"
import { parseDate } from "@/utils"


export const initHolesArray = (cols: number, rows: number): HoleDescription[] => {
    const holesArray: HoleDescription[] = []
    for (let i = 0; i < cols * rows; i++) {
        holesArray.push({
            ...structuredClone(defaultHoleDescription),
            hole_number: String.fromCharCode(Math.ceil((i + 1) / cols) + 64) + ((i % rows) + 1)
        })
    }
    return holesArray
}

export const calculateSummary = (matrixData: MatrixData): SummaryData => {
    if (!matrixData) {
        return {
            calculatedProduction: {
                drilled: 0,
                drilledRemote: 0,
                redrilled: 0,
                redrilledRemote: 0,
                cleaned: 0,
                cleanedRemote: 0
            },
            realProduction: {
                drilled: 0,
                drilledRemote: 0,
                redrilled: 0,
                redrilledRemote: 0,
                cleaned: 0,
                cleanedRemote: 0
            }
        }
    }

    const calculatedProduction = matrixData?.holeDescriptions.reduce((acc, hole) => {
        if (hole.is_remote) {
            return {
                drilled: acc.drilled,
                drilledRemote: acc.drilledRemote + (hole.drilled.use_for_calculations ? hole.drilled.value : (hole.drilled.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0)),
                redrilled: acc.redrilled,
                redrilledRemote: acc.redrilledRemote + (hole.redrilled.use_for_calculations ? hole.redrilled.value : (hole.redrilled.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0)),
                cleaned: acc.cleaned,
                cleanedRemote: acc.cleanedRemote + (hole.cleaned.use_for_calculations ? hole.cleaned.value : (hole.cleaned.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0))
            }
        } else {
            return {
                drilled: acc.drilled + (hole.drilled.use_for_calculations ? hole.drilled.value : (hole.drilled.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0)),
                drilledRemote: acc.drilledRemote,
                redrilled: acc.redrilled + (hole.redrilled.use_for_calculations ? hole.redrilled.value : (hole.redrilled.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0)),
                redrilledRemote: acc.redrilledRemote,
                cleaned: acc.cleaned + (hole.cleaned.use_for_calculations ? hole.cleaned.value : (hole.cleaned.value != 0 && hole.depth_planned.use_for_calculations ? hole.depth_planned.value : 0)),
                cleanedRemote: acc.cleanedRemote
            }
        }
    }, {
        drilled: 0,
        drilledRemote: 0,
        redrilled: 0,
        redrilledRemote: 0,
        cleaned: 0,
        cleanedRemote: 0
    })

    const realProduction = matrixData?.holeDescriptions.reduce((acc, hole) => {
        return {
            drilled: acc.drilled + (hole.is_remote ? 0 : hole.drilled.value),
            drilledRemote: acc.drilledRemote + (hole.is_remote ? hole.drilled.value : 0),
            redrilled: acc.redrilled + (hole.is_remote ? 0 : hole.redrilled.value),
            redrilledRemote: acc.redrilledRemote + (hole.is_remote ? hole.redrilled.value : 0),
            cleaned: acc.cleaned + (hole.is_remote ? 0 : hole.cleaned.value),
            cleanedRemote: acc.cleanedRemote + (hole.is_remote ? hole.cleaned.value : 0)
        }
    }, {
        drilled: 0,
        drilledRemote: 0,
        redrilled: 0,
        redrilledRemote: 0,
        cleaned: 0,
        cleanedRemote: 0
    })

    return {
        calculatedProduction: {
            drilled: parseFloat(calculatedProduction.drilled.toFixed(2)),
            drilledRemote: parseFloat(calculatedProduction.drilledRemote.toFixed(2)),
            redrilled: parseFloat(calculatedProduction.redrilled.toFixed(2)),
            redrilledRemote: parseFloat(calculatedProduction.redrilledRemote.toFixed(2)),
            cleaned: parseFloat(calculatedProduction.cleaned.toFixed(2)),
            cleanedRemote: parseFloat(calculatedProduction.cleanedRemote.toFixed(2))
        },
        realProduction: {
            drilled: parseFloat(realProduction.drilled.toFixed(2)),
            drilledRemote: parseFloat(realProduction.drilledRemote.toFixed(2)),
            redrilled: parseFloat(realProduction.redrilled.toFixed(2)),
            redrilledRemote: parseFloat(realProduction.redrilledRemote.toFixed(2)),
            cleaned: parseFloat(realProduction.cleaned.toFixed(2)),
            cleanedRemote: parseFloat(realProduction.cleanedRemote.toFixed(2))
        }
    }
}

export const makeMatrixData = (report?: Api.Report): MatrixData | undefined => {
    if (!report) {
        return undefined
    }
    const form = report.form
    const cols = form.matrix ? form.matrix.length : 0
    const rows = form.matrix ? form.matrix[0]?.length : 0
    const holeDescriptions: HoleDescription[] = []
    form.mine?.forEach((hole: any) => {
        holeDescriptions.push({
            hole_number: hole.hole_number,
            is_off_contract: hole.is_off_contract,
            trephine_number: hole.trephine_number,
            diameter: parseFloat(hole.diameter),
            hammer: hole.hammer,
            angle: parseFloat(hole.angle),
            depth_planned: {
                value: parseFloat(hole.depth_planned.value),
                use_for_calculations: hole.depth_planned.use_for_calculations
            },
            drilled: {
                value: parseFloat(hole.drilled.value),
                use_for_calculations: hole.drilled.use_for_calculations
            },
            redrilled: {
                value: parseFloat(hole.redrilled.value),
                use_for_calculations: hole.redrilled.use_for_calculations
            },
            cleaned: {
                value: parseFloat(hole.cleaned.value),
                use_for_calculations: hole.cleaned.use_for_calculations
            },
            depth_measured: parseFloat(hole.depth_measured),
            broken_rocks: parseFloat(hole.broken_rocks),
            casing_meters: parseFloat(hole.casing_meters),
            is_remote: hole.is_remote ? true : false,
            is_water: hole.is_water ? true : false,
            is_pre_cutting: hole.is_pre_cutting ? true : false,
            is_geotechnical_limit: hole.is_geotechnical_limit ? true : false,
            result: hole.result,
            comment: hole.comment,
            matrix_input: hole.matrix_input,
            is_collapsed: true,
            is_hole_number_changed: true,
            supervisor: hole.supervisor ?? ''
        })
    })

    const matrixData: MatrixData = {
        cols: cols,
        rows: rows,
        burden: form.burden ?? 0,
        spacing: form.spacing ?? 0,
        holeDescriptions: holeDescriptions.length == 0 ? initHolesArray(cols, rows) : holeDescriptions,
    }

    return matrixData
}

export const makeDrillEquipment = (equipmentList: Api.Equipment[], report?: Api.Report): DrillEquipment => {
    if (!report) {
        return {
            compressor: {
                equipment_id: undefined,
                start_time: 0,
                end_time: 0
            },
            drill: {
                equipment_id: undefined,
                start_time: 0,
                end_time: 0
            },
            truck: {
                equipment_id: undefined,
                start_time: 0,
                end_time: 0
            }
        }
    }

    const equipments = report.equipment ? report.equipment : report.equipments

    const drill = equipments?.filter(e => e.status?.slug === 'drilling-machine' || e.status?.slug === 'air-drill').at(0)
    const compressor = equipments?.filter(e => e.status?.slug === 'compressor').at(0)
    const truck = equipments?.filter(e => e.status?.slug === 'truck').at(0)

    const drillEquipment: DrillEquipment = {
        drill: {
            equipment_id: report.equipment ? drill?.id : parseInt(drill?.equipment_id),
            start_time: report.equipment ? drill?.pivot?.start_time ?? 0 : parseInt(drill?.start_time) || 0,
            end_time: report.equipment ? drill?.pivot?.end_time ?? 0 : parseInt(drill?.end_time) || 0,
            status: equipmentList?.find(e => e.id == (report.equipment ? drill?.id : drill?.equipment_id))?.status
        },
        compressor: {
            equipment_id: report.equipment ? compressor?.id : parseInt(compressor?.equipment_id),
            start_time: report.equipment ? compressor?.pivot?.start_time ?? 0 : parseInt(compressor?.start_time) || 0,
            end_time: report.equipment ? compressor?.pivot?.end_time ?? 0 : parseInt(compressor?.end_time) || 0,
            status: equipmentList?.find(e => e.id == (report.equipment ? compressor?.id : compressor?.equipment_id))?.status
        },
        truck: {
            equipment_id: report.equipment ? truck?.id : parseInt(truck?.equipment_id),
            start_time: report.equipment ? truck?.pivot?.start_time ?? 0 : parseInt(truck?.start_time) || 0,
            end_time: report.equipment ? truck?.pivot?.end_time ?? 0 : parseInt(truck?.end_time) || 0,
            status: equipmentList?.find(e => e.id == (report.equipment ? truck?.id : truck?.equipment_id))?.status
        },
    }

    return drillEquipment
}

export const makeGeneralInformation = (tasks: Api.Task[], report?: Api.Report): GeneralInformation => {
    if (!report) {
        return {
            date_in: DateTime.now(),
            project_id: undefined,
            form: {
                drilling_block: '',
                ditch_number: '',
                quarter: 'day',
                drill_type: 'mine',
                units: 'meters',
                time_entries: []
            }
        }
    }

    return {
        date_in: parseDate(report.date_in),
        project_id: report.project_id,
        form: {
            drilling_block: report.form.drilling_block ?? '',
            ditch_number: report.form.ditch_number ?? '',
            quarter: report.form.quarter ?? 'day',
            drill_type: report.form.drill_type ?? 'mine',
            units: report.form.units ?? 'meters',
            time_entries: report.form.time_entries?.map((entry: {
                myoctans_task_id: string
                task_id: string
                work_order_time: string
                traveling_time?: string
            }) => ({
                myoctans_task_id: parseInt(entry.myoctans_task_id),
                task_id: parseInt(entry.task_id),
                work_order_time: parseFloat(entry.work_order_time ?? '0'),
                traveling_time: parseFloat(entry.traveling_time ?? '0'),
                equipment_categories: tasks.find(t => t.id == parseInt(entry.task_id))?.equipment_categories || [],
                BT: tasks.find(t => t.id == parseInt(entry.task_id))?.BT || ''
            })
            )
        }
    }
}